import { useEffect, useState } from 'react';

// A Save press that shows its spinner before the page behind it re-renders.
//
// Pressing Save sets a flag on the review page, and that page re-renders every
// card in the queue - ten of them, each the size of the modal being saved -
// before the browser paints again. The spinner is part of that paint, so it
// arrived at the end of the freeze rather than at the start of it, and the
// press looked like it had been missed.
//
// Holding the parent's work until the next frame puts the spinner on screen
// first: the modal alone re-renders, the browser paints, and the expensive
// update happens after. The flag is the modal's own, because the parent's does
// not turn on until the work it is waiting for has already begun.
//
// Everything the press puts out of use reads this flag rather than the
// parent's - the boxes, the add and remove buttons, the reloads and the
// closing of the modal - or the button would spin over fields still taking
// text whose edit was already on its way to being thrown away.
export function usePressToSave(isSaving: boolean, open: boolean) {
  const [pressed, setPressed] = useState(false);

  // The press is over when the parent stops saving, and a modal opened again is
  // opened on a press nobody made.
  useEffect(() => {
    if (!open) setPressed(false);
  }, [open]);

  useEffect(() => {
    if (!isSaving) setPressed(false);
  }, [isSaving]);

  const press = (save: () => void) => {
    if (pressed || isSaving) return;
    setPressed(true);
    requestAnimationFrame(save);
  };

  return { saving: isSaving || pressed, press };
}
